import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { useAppContext } from "../../providers/ProviderAppContainer";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import Loading from "../common/Loading";
import UserSearch from "../common/UserSearch";
import { AUTHORITIES } from "../../constants";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";

const WEEKDAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function parseTimeToDate(time) {
    if (!time) return null;
    const raw = String(time).slice(0, 8);
    const [hh = "0", mm = "0"] = raw.split(":");
    const date = new Date();
    date.setHours(Number(hh), Number(mm), 0, 0);
    return date;
}

function formatTime(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function emptyDay(weekday) {
    return {
        weekday,
        start_time: null,
        end_time: null,
        subject: "",
        event_handler: null,
        event_handler_user: null,
    };
}

function dayFromRow(weekday, row = {}) {
    const event_handler = row.event_handler != null ? Number(row.event_handler) : null;
    return {
        weekday,
        start_time: row.start_time || null,
        end_time: row.end_time || null,
        subject: row.subject || "",
        event_handler,
        event_handler_user: event_handler
            ? { id: event_handler, full_name: row.event_handler_name || `User #${event_handler}` }
            : null,
    };
}

export default function BatchScheduleEditor({ batchId }) {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [days, setDays] = useState(WEEKDAY_LABELS.map((_, weekday) => emptyDay(weekday)));

    useEffect(() => {
        if (!batchId) return;
        requestAPI({
            requestPath: `batches/${batchId}/schedule`,
            requestMethod: "GET",
            setLoading,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200 && Array.isArray(data)) {
                    setDays(
                        WEEKDAY_LABELS.map((_, weekday) => {
                            const row = data.find((item) => Number(item.weekday) === weekday) || {};
                            return dayFromRow(weekday, row);
                        }),
                    );
                }
            },
        });
    }, [batchId, requestAPI]);

    const updateDay = useCallback((weekday, patch) => {
        setDays((prev) => prev.map((item) => (item.weekday === weekday ? { ...item, ...patch } : item)));
    }, []);

    const save = useCallback(() => {
        for (const day of days) {
            const hasTimes = !!(day.start_time && day.end_time);
            if ((day.start_time && !day.end_time) || (!day.start_time && day.end_time)) {
                showToast({
                    severity: "error",
                    summary: "Failed",
                    detail: `${WEEKDAY_LABELS[day.weekday]}: set both start and end time`,
                    life: 2500,
                });
                return;
            }
            if (hasTimes && !String(day.subject || "").trim()) {
                showToast({
                    severity: "error",
                    summary: "Failed",
                    detail: `${WEEKDAY_LABELS[day.weekday]}: subject is required`,
                    life: 2500,
                });
                return;
            }
            if (hasTimes && !day.event_handler) {
                showToast({
                    severity: "error",
                    summary: "Failed",
                    detail: `${WEEKDAY_LABELS[day.weekday]}: teacher (event handler) is required`,
                    life: 2500,
                });
                return;
            }
        }

        const payload = {
            days: days.map((day) => {
                const hasTimes = !!(day.start_time && day.end_time);
                const event_handler = hasTimes && day.event_handler != null ? Number(day.event_handler) : null;
                return {
                    weekday: day.weekday,
                    start_time: day.start_time || null,
                    end_time: day.end_time || null,
                    subject: hasTimes ? String(day.subject || "").trim() : null,
                    event_handler: Number.isFinite(event_handler) && event_handler > 0 ? event_handler : null,
                };
            }),
        };

        requestAPI({
            requestPath: `batches/${batchId}/schedule`,
            requestMethod: "PUT",
            requestPostBody: payload,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed to save schedule", life: 2000 }),
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200 && Array.isArray(data)) {
                    const missingFields = data.some(
                        (row) =>
                            row?.start_time &&
                            row?.end_time &&
                            (row.subject == null || row.subject === "" || row.event_handler == null),
                    );
                    if (missingFields) {
                        showToast({
                            severity: "error",
                            summary: "API outdated",
                            detail: "Times saved, but subject/teacher were ignored. Restart/redeploy sahas_api with latest schedule changes.",
                            life: 4000,
                        });
                        return;
                    }
                    setDays(
                        WEEKDAY_LABELS.map((_, weekday) => {
                            const row = data.find((item) => Number(item.weekday) === weekday) || {};
                            return dayFromRow(weekday, row);
                        }),
                    );
                    showToast({ severity: "success", summary: "Saved", detail: "Schedule updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: data?.error || "Failed to save schedule", life: 2000 });
                }
            },
        });
    }, [batchId, days, requestAPI, showToast]);

    if (loading) {
        return <Loading message="Loading schedule" />;
    }

    return (
        <div className="flex flex-column gap-3 border-1 border-gray-200 border-round p-3">
            <div className="flex align-items-center justify-content-between gap-2">
                <span className={`${TEXT_NORMAL} font-semibold`}>Weekly Schedule</span>
                <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_BATCH}>
                    <Button label="Save Schedule" size="small" severity="warning" loading={saving} onClick={save} />
                </HasRequiredAuthority>
            </div>

            {days.map((day) => (
                <div key={day.weekday} className="flex flex-column gap-2 border-bottom-1 border-gray-100 pb-3">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <span className={`${TEXT_SMALL} font-semibold`}>{WEEKDAY_LABELS[day.weekday]}</span>
                        <Button
                            label="Clear"
                            size="small"
                            text
                            disabled={saving}
                            onClick={() =>
                                updateDay(day.weekday, {
                                    start_time: null,
                                    end_time: null,
                                    subject: "",
                                    event_handler: null,
                                    event_handler_user: null,
                                })
                            }
                        />
                    </div>
                    <div className="flex gap-2">
                        <Calendar
                            value={parseTimeToDate(day.start_time)}
                            onChange={(e) => updateDay(day.weekday, { start_time: formatTime(e.value) })}
                            timeOnly
                            hourFormat="24"
                            placeholder="Start"
                            className="w-full"
                            disabled={saving}
                        />
                        <Calendar
                            value={parseTimeToDate(day.end_time)}
                            onChange={(e) => updateDay(day.weekday, { end_time: formatTime(e.value) })}
                            timeOnly
                            hourFormat="24"
                            placeholder="End"
                            className="w-full"
                            disabled={saving}
                        />
                    </div>
                    <InputText
                        value={day.subject}
                        onChange={(e) => updateDay(day.weekday, { subject: e.target.value })}
                        placeholder="Subject"
                        className="w-full"
                        disabled={saving}
                    />
                    <UserSearch
                        value={day.event_handler_user}
                        onChange={(user) =>
                            updateDay(day.weekday, {
                                event_handler_user: user || null,
                                event_handler: user?.id != null ? Number(user.id) : null,
                            })
                        }
                        placeholder="Teacher"
                        label="Teacher"
                        disabled={saving}
                        showClear
                        inputId={`schedule-handler-${day.weekday}`}
                    />
                </div>
            ))}
        </div>
    );
}
